import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell, MessageSquare, AtSign, CheckCircle2, UserPlus, Users, Clock, AlertTriangle, Star, Database,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useNotificationStore } from '@/stores/notificationStore'
import { announce } from '@/components/ui/AriaLiveRegion'
import type { NotificationType } from '@/lib/types'
import type { LucideIcon } from 'lucide-react'

const typeConfig: Record<NotificationType, { icon: LucideIcon; color: string; bg: string; label: string }> = {
  'task-due': { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-500', label: '마감 알림' },
  'task-overdue': { icon: AlertTriangle, color: 'text-rose-500', bg: 'bg-rose-500', label: '기한 초과' },
  'task-reminder': { icon: Bell, color: 'text-amber-500', bg: 'bg-amber-500', label: '리마인더' },
  'backup-reminder': { icon: Database, color: 'text-gray-400', bg: 'bg-gray-500', label: '백업' },
  'streak-milestone': { icon: Star, color: 'text-amber-500', bg: 'bg-amber-500', label: '연속 달성' },
  'shared-task-created': { icon: Users, color: 'text-blue-500', bg: 'bg-blue-500', label: '공유 작업' },
  'chat-message': { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-500', label: '채팅' },
  'chat-mention': { icon: AtSign, color: 'text-violet-500', bg: 'bg-violet-500', label: '멘션' },
  'shared-task-completed': { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500', label: '완료' },
  'member-joined': { icon: UserPlus, color: 'text-teal-500', bg: 'bg-teal-500', label: '새 멤버' },
}

/** 채팅/그룹 관련 알림인지 판별 */
function isChatRelated(type: NotificationType): boolean {
  return type === 'chat-message' || type === 'chat-mention' || type === 'shared-task-created'
    || type === 'shared-task-completed' || type === 'member-joined'
}

export function NotificationToast() {
  const latest = useNotificationStore((s) => s.latestNotification)
  const dequeueToast = useNotificationStore((s) => s.dequeueToast)
  const [visible, setVisible] = useState(false)
  const [exiting, setExiting] = useState(false)
  const navigate = useNavigate()
  // 포커스/호버 시 자동 닫힘 타이머를 일시 정지하기 위한 참조
  const exitTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const pausedRef = useRef(false)

  const clearTimers = () => {
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    exitTimerRef.current = undefined
    hideTimerRef.current = undefined
  }

  const startDismissTimers = () => {
    clearTimers()
    exitTimerRef.current = setTimeout(() => setExiting(true), 3500)
    hideTimerRef.current = setTimeout(() => {
      setVisible(false)
      dequeueToast()
    }, 3800)
  }

  useEffect(() => {
    if (latest) {
      setVisible(true)
      setExiting(false)
      pausedRef.current = false
      // 항상 마운트된 전역 라이브 리전으로 알림 내용을 읽어줌 (멘션은 즉시)
      const announcement = `${latest.senderName || latest.title}: ${latest.message}`
      announce(announcement, latest.type === 'chat-mention' ? 'assertive' : 'polite')
      startDismissTimers()
      return () => clearTimers()
    } else {
      setVisible(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest?.id, dequeueToast])

  if (!visible || !latest) return null

  const config = typeConfig[latest.type] || typeConfig['task-reminder']
  const Icon = config.icon

  // 썸네일: 발신자 프로필 > 그룹 이미지 > 아이콘 폴백
  const thumbnailUrl = latest.senderPhotoURL || latest.groupThumbnailUrl
  const hasThumbnail = !!thumbnailUrl
  const isChatType = isChatRelated(latest.type)
  const ariaLabel = `${latest.senderName || latest.title}: ${latest.message}`

  const handleClick = () => {
    useNotificationStore.getState().markAsRead(latest.id)
    if (latest.groupId) {
      const goToChat = latest.type === 'chat-message' || latest.type === 'chat-mention'
      navigate(goToChat ? `/groups/shared/${latest.groupId}/chat` : `/groups/shared/${latest.groupId}`)
    } else if (latest.taskId) {
      navigate(`/task/${latest.taskId}`)
    }
    clearTimers()
    setVisible(false)
  }

  // 포커스/호버 동안 자동 닫힘을 멈춰 키보드 사용자가 활성화할 시간을 확보
  const handlePause = () => {
    pausedRef.current = true
    clearTimers()
  }

  const handleResume = () => {
    if (!pausedRef.current) return
    pausedRef.current = false
    setExiting(false)
    startDismissTimers()
  }

  return (
    <div
      role="status"
      aria-live={latest.type === 'chat-mention' ? 'assertive' : 'polite'}
      className={clsx(
        'fixed top-0 left-0 right-0 z-[201] pointer-events-none',
        exiting
          ? 'animate-[slideOutTop_0.2s_ease-in_forwards]'
          : 'animate-[slideInFromTop_0.25s_ease-out]'
      )}
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4px)' }}
    >
      <div className="mx-3 mt-2 pointer-events-auto">
        <button
          type="button"
          onClick={handleClick}
          onMouseEnter={handlePause}
          onMouseLeave={handleResume}
          onFocus={handlePause}
          onBlur={handleResume}
          aria-label={ariaLabel}
          className="block w-full text-left bg-gray-900/95 backdrop-blur-md rounded-2xl shadow-2xl cursor-pointer active:scale-[0.98] transition-transform overflow-hidden"
        >
          <div className="px-3.5 py-3 flex items-start gap-3" aria-hidden="true">

            {/* 썸네일: 프로필 사진 또는 아이콘 */}
            {hasThumbnail ? (
              <img
                src={thumbnailUrl}
                alt=""
                className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-1 ring-white/20"
              />
            ) : (
              <div className={clsx(
                'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                isChatType ? 'bg-primary-500' : config.bg
              )}>
                <Icon className="w-5 h-5 text-white" />
              </div>
            )}

            {/* 콘텐츠 */}
            <div className="flex-1 min-w-0 pt-0.5">
              {/* 상단: 발신자/앱 이름 + 시간 */}
              <div className="flex items-center justify-between gap-2">
                <span className="text-label font-bold text-white truncate">
                  {latest.senderName || latest.title}
                </span>
                <span className="text-meta text-gray-400 flex-shrink-0">
                  지금
                </span>
              </div>

              {/* 그룹명 (채팅 관련 + senderName이 있을 때) */}
              {isChatType && latest.senderName && latest.title !== latest.senderName && (
                <p className="text-meta text-gray-400 truncate mt-0.5">
                  {latest.title}
                </p>
              )}

              {/* 메시지 본문 */}
              <p className="text-label text-gray-200 line-clamp-2 mt-0.5 leading-snug">
                {latest.message}
              </p>
            </div>

          </div>
        </button>
      </div>
    </div>
  )
}
